/**
 * MerchManager — Admin approval workflow for American Hospital Prices store
 *
 * Tabs:
 *   Queue    → pending_review products awaiting approve/deny
 *   Live     → approved products (edit price, badge, archive)
 *   Denied   → denied products with feedback + restore
 *   Ideas    → 28 product idea tiles + generation queue (drafts)
 *   All      → full catalog table
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle, XCircle, Edit3, Plus, RefreshCw, Copy,
  RotateCcw, Archive, Tag, TrendingUp, Package, X, AlertTriangle,
  ChevronDown, ChevronUp, ExternalLink,
} from "lucide-react";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const T = {
  bg:        "#FAF9F6",
  navy:      "#002868",
  red:       "#B22234",
  gold:      "#C5A55A",
  green:     "#2b5e49",
  text:      "#1B1B1B",
  textMuted: "rgba(27,27,27,0.62)",
  border:    "rgba(27,27,27,0.12)",
  cardBg:    "#FFFFFF",
  shadow:    "0 2px 12px rgba(0,0,0,0.07)",
  shadowMd:  "0 4px 24px rgba(0,0,0,0.10)",
};
const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans  = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ─── Types ────────────────────────────────────────────────────────────────────
type Status = 'draft' | 'pending_review' | 'approved' | 'denied' | 'archived';

interface MerchProduct {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category_slug: string;
  brand: string;
  status: Status;
  price: number | null;
  cost_estimate: number | null;
  image_path: string | null;
  badge: string | null;
  printify_product_id: string | null;
  generation_prompt: string | null;
  generation_notes: string | null;
  sort_order: number;
  created_at: string;
  approved_at: string | null;
  denied_at: string | null;
}

interface MerchReview {
  id: string;
  product_id: string;
  reviewer_name: string;
  action: string;
  price_set: number | null;
  feedback: string | null;
  created_at: string;
}

type SubTab = 'queue' | 'live' | 'denied' | 'ideas' | 'mottos' | 'all';

type MottoStatus = 'draft' | 'approved' | 'denied' | 'archived';
type MottoCategory = 'general' | 'tagline' | 'bumper' | 'apparel' | 'social' | 'hashtag' | 'call_to_action';

interface BrandMotto {
  id: string;
  brand: string;
  motto: string;
  category: MottoCategory;
  submitted_by: string;
  status: MottoStatus;
  votes: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const CAT: Record<string, { icon: string; label: string; color: string }> = {
  stickers:    { icon: '🏷️', label: 'Stickers & Decals',   color: '#B22234' },
  apparel:     { icon: '👕', label: 'Apparel',              color: '#002868' },
  headwear:    { icon: '🧢', label: 'Headwear',             color: '#4A3728' },
  drinkware:   { icon: '☕', label: 'Drinkware',            color: '#6B4C8A' },
  bags:        { icon: '🛍️', label: 'Bags & Totes',        color: '#2b5e49' },
  prints:      { icon: '🖼️', label: 'Art & Prints',        color: '#D4763B' },
  home:        { icon: '🏠', label: 'Home & Office',        color: '#1A5F7A' },
  accessories: { icon: '📎', label: 'Accessories',          color: '#7B6E28' },
  novelty:     { icon: '📣', label: 'Novelty & Activist',   color: '#8B2252' },
  other:       { icon: '📦', label: 'Other',                color: '#555555' },
};

const BADGES = ['', 'New', 'Best Seller', 'Fan Favorite', 'Sale', 'Limited'];

const DENY_CHIPS = [
  'Wrong design style', 'Poor image quality', 'Wrong product type',
  'Branding not right', 'Colors off', 'Not a priority', 'Other',
];

const PRODUCT_IDEAS = [
  // Stickers
  { cat: 'stickers', name: 'Car Magnet',        desc: '4"×6" outdoor magnet, AHP shield. Remove/reapply anytime.',          price: 8.99,  cost: 3.50, vibe: '🚗 Goes on anything magnetic' },
  { cat: 'stickers', name: 'Window Cling',       desc: '5"×5" static cling, no adhesive. Perfect for car windows.',           price: 6.99,  cost: 2.00, vibe: '🪟 Easy on, easy off' },
  { cat: 'stickers', name: 'Yard Sign (18"×24")',desc: 'Corrugated plastic + metal H-stake. "Know Your Price Before You Go"', price: 19.99, cost: 8.00, vibe: '📣 Activate the neighborhood' },
  // Apparel
  { cat: 'apparel',  name: 'Pullover Hoodie',    desc: 'Heavyweight unisex hoodie with AHP logo. S–3XL.',                     price: 44.99, cost: 24.00, vibe: '🏥 Rally-ready comfort' },
  { cat: 'apparel',  name: "Women's Fitted Tee", desc: "Slim-fit women's cut, softer weight than unisex.",                    price: 27.99, cost: 11.00, vibe: '💪 Fitted, not boxy' },
  { cat: 'apparel',  name: 'Long Sleeve Shirt',  desc: 'Unisex long sleeve, AHP logo on chest. Great for fall.',              price: 32.99, cost: 13.00, vibe: '🍂 Layer up season' },
  { cat: 'apparel',  name: 'Baby Onesie',        desc: '"Born into $400k in healthcare debt" — softest cotton, 0–18mo.',      price: 22.99, cost: 9.00,  vibe: '🍼 Maximum virality potential' },
  { cat: 'apparel',  name: 'Crew Socks',         desc: 'Dollar sign + hospital price pattern, cushioned, one size.',          price: 16.99, cost: 6.00,  vibe: '🧦 Walk the talk' },
  // Headwear
  { cat: 'headwear', name: 'Snapback Cap',        desc: 'Flat-brim snapback in navy or red, embroidered AHP shield.',         price: 32.99, cost: 20.00, vibe: '🎤 Campaign trail energy' },
  { cat: 'headwear', name: 'Beanie',              desc: 'Knit beanie in navy with embroidered AHP logo patch. One size.',     price: 24.99, cost: 12.00, vibe: '❄️ Cold weather advocacy' },
  // Drinkware
  { cat: 'drinkware',name: 'Pint Glass',          desc: '"Healthcare Cheers" — 16oz clear pint glass with AHP price stat.',   price: 19.99, cost: 7.00,  vibe: '🍺 Great conversation starter' },
  { cat: 'drinkware',name: 'Insulated Tumbler',   desc: '20oz stainless steel, keeps cold 24h/hot 12h, AHP branding.',        price: 32.99, cost: 15.00, vibe: '☕ Daily driver merch' },
  { cat: 'drinkware',name: 'Water Bottle (32oz)', desc: 'BPA-free with AHP shield and a real hospital price stat on the side.',price: 24.99, cost: 10.00, vibe: '💧 Hydrate the revolution' },
  // Bags
  { cat: 'bags',     name: 'Canvas Backpack',     desc: 'Canvas backpack, AHP patch on front pocket, laptop sleeve. 20L.',   price: 44.99, cost: 22.00, vibe: '🎒 Student/professional carry' },
  // Prints
  { cat: 'prints',   name: 'Price Flag Poster 11×17', desc: 'The healthcare price flag as a semi-gloss poster. Ready to frame.', price: 18.99, cost: 5.00, vibe: '🖼️ Statement piece for any wall' },
  { cat: 'prints',   name: 'Price Flag Poster 18×24', desc: 'Large format price flag, 80lb matte. Museum quality.',           price: 29.99, cost: 9.00,  vibe: '🖼️ Big statement, big wall' },
  { cat: 'prints',   name: 'Report Card Wall Art',    desc: 'Infographic-style "Hospital Report Card" grading methodology.',  price: 24.99, cost: 7.00,  vibe: '📊 Educational + aesthetic' },
  { cat: 'prints',   name: 'Canvas Print (16×20)',    desc: 'Price flag on gallery-wrapped canvas, no frame needed.',         price: 59.99, cost: 28.00, vibe: '🎨 High-end gift option' },
  // Home
  { cat: 'home',     name: 'Notebook / Journal',  desc: '"My Medical Records" — 120-page lined hardcover, AHP branding.',    price: 19.99, cost: 8.00,  vibe: '📓 Track your bills + advocate' },
  { cat: 'home',     name: 'Desk Mat (36×18)',     desc: 'Large desk mat with AHP price data visualization. Non-slip.',       price: 32.99, cost: 14.00, vibe: '🖥️ WFH activist aesthetic' },
  { cat: 'home',     name: 'Coaster Set (4-pack)', desc: '4 hardboard coasters with different AHP price stats. Cork backing.', price: 19.99, cost: 8.00, vibe: '🫖 Conversation with every cup' },
  { cat: 'home',     name: 'Throw Pillow',         desc: '"Rest. You\'ll Need It. At $800/night." — 16"×16" with AHP.',      price: 34.99, cost: 18.00, vibe: '🛋️ Dark humor home goods' },
  // Accessories
  { cat: 'accessories', name: 'iPhone Case',        desc: 'Tough clear or solid navy case with AHP shield. Multiple sizes.',  price: 24.99, cost: 12.00, vibe: '📱 Always in hand, always visible' },
  { cat: 'accessories', name: 'Hard Enamel Lapel Pin', desc: '1.25" hard enamel AHP shield pin, gold plating, butterfly clasp.', price: 12.99, cost: 5.00, vibe: '📌 Subtlest statement piece' },
  { cat: 'accessories', name: 'Acrylic Keychain',   desc: 'Die-cut acrylic AHP shield keychain. Double-sided full color.',    price: 9.99,  cost: 4.00,  vibe: '🔑 Daily carry advocacy' },
  // Novelty
  { cat: 'novelty',  name: 'Postcard Set (10)',     desc: '"Send your rep a healthcare reality check" — 10 political postcards.', price: 12.99, cost: 5.00, vibe: '📮 Direct democracy in your mailbox' },
  { cat: 'novelty',  name: 'Pinback Button Set (5)',desc: '2.25" campaign-style pinback buttons with 5 AHP slogans.',         price: 9.99,  cost: 4.00,  vibe: '🔵 Old-school political energy' },
  { cat: 'novelty',  name: 'Playing Cards',         desc: '"Hospital Price Game" — 52-card deck with AHP data design.',       price: 19.99, cost: 9.00,  vibe: '🃏 Play with hospital data' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function margin(price: number | null, cost: number | null): number | null {
  if (!price || !cost || price <= 0) return null;
  return Math.round(((price - cost) / price) * 100);
}

function marginColor(pct: number | null): string {
  if (pct === null) return T.textMuted;
  if (pct >= 50) return T.green;
  if (pct >= 30) return '#B8860B';
  return T.red;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function slugify(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

// ─── Shared Modal Wrapper ─────────────────────────────────────────────────────
function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.55)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 720,
          maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '20px 24px 16px', borderBottom: `1px solid ${T.border}`,
    }}>
      <div style={{ fontFamily: serif, fontSize: 22, color: T.navy }}>{title}</div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, padding: 4 }}>
        <X size={20} />
      </button>
    </div>
  );
}

// ─── Approve Modal ────────────────────────────────────────────────────────────
function ApproveModal({
  product, reviewerName, onClose, onApprove,
}: {
  product: MerchProduct;
  reviewerName: string;
  onClose: () => void;
  onApprove: (p: { price: number; badge: string; name: string; tagline: string; reviewer: string }) => void;
}) {
  const [name, setName]         = useState(product.name);
  const [tagline, setTagline]   = useState(product.tagline);
  const [price, setPrice]       = useState(String(product.price ?? ''));
  const [badge, setBadge]       = useState(product.badge ?? '');
  const [reviewer, setReviewer] = useState(reviewerName);
  const cat = CAT[product.category_slug] ?? CAT.other;
  const priceNum = parseFloat(price) || null;
  const mgn = margin(priceNum, product.cost_estimate);

  return (
    <Modal onClose={onClose}>
      <ModalHeader title="Approve Product" onClose={onClose} />
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 0 }}>
        {/* Image */}
        <div style={{ background: 'rgba(0,40,104,0.04)', borderRight: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 280 }}>
          {product.image_path ? (
            <img src={product.image_path} alt={product.name} style={{ width: '100%', height: 280, objectFit: 'cover' }} />
          ) : (
            <div style={{ textAlign: 'center', color: T.textMuted }}>
              <Package size={40} style={{ opacity: 0.3, marginBottom: 8 }} />
              <div style={{ fontFamily: sans, fontSize: 12 }}>No image yet</div>
            </div>
          )}
        </div>

        {/* Form */}
        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Category */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: `${cat.color}18`, borderRadius: 20, padding: '4px 12px', width: 'fit-content' }}>
            <span style={{ fontSize: 13 }}>{cat.icon}</span>
            <span style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: cat.color }}>{cat.label}</span>
          </div>

          {/* Name */}
          <div>
            <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>PRODUCT NAME</label>
            <input value={name} onChange={e => setName(e.target.value)} style={{ width: '100%', fontFamily: sans, fontSize: 14, padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, background: T.bg, boxSizing: 'border-box' }} />
          </div>

          {/* Tagline */}
          <div>
            <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>TAGLINE</label>
            <input value={tagline} onChange={e => setTagline(e.target.value)} style={{ width: '100%', fontFamily: sans, fontSize: 13, padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, background: T.bg, boxSizing: 'border-box' }} />
          </div>

          {/* Price */}
          <div>
            <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>SALE PRICE ($)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="number" step="0.01" min="0" value={price}
                onChange={e => setPrice(e.target.value)}
                style={{ width: 100, fontFamily: sans, fontSize: 15, fontWeight: 700, padding: '8px 10px', border: `2px solid ${T.navy}`, borderRadius: 6, color: T.navy, background: '#fff', boxSizing: 'border-box' }}
              />
              {product.cost_estimate && (
                <div style={{ fontFamily: sans, fontSize: 12, color: T.textMuted }}>
                  Cost: <b>${product.cost_estimate.toFixed(2)}</b>
                  {mgn !== null && (
                    <span style={{ marginLeft: 8, fontWeight: 700, color: marginColor(mgn) }}>
                      {mgn}% margin
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Badge */}
          <div>
            <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>BADGE (optional)</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {BADGES.map(b => (
                <button key={b || 'none'} onClick={() => setBadge(b)} style={{
                  padding: '5px 12px', borderRadius: 20, fontFamily: sans, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: badge === b ? `2px solid ${T.navy}` : `1px solid ${T.border}`,
                  background: badge === b ? T.navy : '#fff',
                  color: badge === b ? '#FAF9F6' : T.textMuted,
                }}>
                  {b || 'None'}
                </button>
              ))}
            </div>
          </div>

          {/* Reviewer */}
          <div>
            <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>YOUR NAME</label>
            <input value={reviewer} onChange={e => setReviewer(e.target.value)} style={{ width: '100%', fontFamily: sans, fontSize: 13, padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, background: T.bg, boxSizing: 'border-box' }} />
          </div>

          {/* Submit */}
          <button
            disabled={!priceNum || !name.trim()}
            onClick={() => onApprove({ price: priceNum!, badge, name: name.trim(), tagline: tagline.trim(), reviewer: reviewer.trim() || 'Admin' })}
            style={{
              marginTop: 4, padding: '12px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: (!priceNum || !name.trim()) ? '#ccc' : T.green,
              color: '#fff', fontFamily: sans, fontSize: 14, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <CheckCircle size={17} />
            Approve &amp; Publish
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Deny Modal ───────────────────────────────────────────────────────────────
function DenyModal({
  product, reviewerName, onClose, onDeny,
}: {
  product: MerchProduct;
  reviewerName: string;
  onClose: () => void;
  onDeny: (p: { feedback: string; aiNotes: string; chips: string[]; reviewer: string }) => void;
}) {
  const [chips, setChips]       = useState<string[]>([]);
  const [feedback, setFeedback] = useState('');
  const [aiNotes, setAiNotes]   = useState('');
  const [reviewer, setReviewer] = useState(reviewerName);
  const [copied, setCopied]     = useState(false);
  const cat = CAT[product.category_slug] ?? CAT.other;
  const slug = slugify(product.name);

  const toggleChip = (chip: string) =>
    setChips(prev => prev.includes(chip) ? prev.filter(c => c !== chip) : [...prev, chip]);

  const geminiCmd = `python3 scripts/gemini-merch-mockups.py --product ${slug}`;

  const copyCmd = () => {
    navigator.clipboard.writeText(geminiCmd);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader title="Deny Product — Give Feedback" onClose={onClose} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Product summary */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', background: T.bg, borderRadius: 8, padding: '12px 14px', border: `1px solid ${T.border}` }}>
          {product.image_path && (
            <img src={product.image_path} alt={product.name} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }} />
          )}
          <div>
            <div style={{ fontFamily: serif, fontSize: 17, color: T.navy }}>{product.name}</div>
            <div style={{ fontFamily: sans, fontSize: 12, color: T.textMuted, marginTop: 2 }}>
              {cat.icon} {cat.label}
            </div>
          </div>
        </div>

        {/* Issue chips */}
        <div>
          <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 8 }}>WHAT'S WRONG? (select all that apply)</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {DENY_CHIPS.map(chip => (
              <button key={chip} onClick={() => toggleChip(chip)} style={{
                padding: '6px 13px', borderRadius: 20, fontFamily: sans, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: chips.includes(chip) ? `2px solid ${T.red}` : `1px solid ${T.border}`,
                background: chips.includes(chip) ? '#FDE8E8' : '#fff',
                color: chips.includes(chip) ? T.red : T.textMuted,
              }}>
                {chip}
              </button>
            ))}
          </div>
        </div>

        {/* Feedback */}
        <div>
          <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>FEEDBACK FOR THE TEAM *</label>
          <textarea
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            placeholder="What specifically didn't work? Be as detailed as possible."
            rows={3}
            style={{ width: '100%', fontFamily: sans, fontSize: 13, padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 6, resize: 'vertical', color: T.text, background: T.bg, boxSizing: 'border-box' }}
          />
        </div>

        {/* AI Notes */}
        <div>
          <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 6 }}>
            NOTES FOR AI REGENERATION <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional — feeds into next Gemini prompt)</span>
          </label>
          <textarea
            value={aiNotes}
            onChange={e => setAiNotes(e.target.value)}
            placeholder="e.g. 'Make the shield bigger', 'Use a real photo not illustration', 'Needs steam from the mug'..."
            rows={3}
            style={{ width: '100%', fontFamily: sans, fontSize: 13, padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 6, resize: 'vertical', color: T.text, background: T.bg, boxSizing: 'border-box' }}
          />
        </div>

        {/* Regenerate command */}
        <div style={{ background: '#1B1B1B', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontFamily: sans, fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.1em', marginBottom: 6 }}>TO REGENERATE THIS PRODUCT IMAGE:</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <code style={{ fontFamily: 'monospace', fontSize: 12, color: '#7EE787', flex: 1 }}>{geminiCmd}</code>
            <button onClick={copyCmd} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 5, padding: '6px 10px', cursor: 'pointer', color: copied ? '#7EE787' : 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Copy size={13} />
              <span style={{ fontFamily: sans, fontSize: 11 }}>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
        </div>

        {/* Reviewer + submit */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>YOUR NAME</label>
            <input value={reviewer} onChange={e => setReviewer(e.target.value)} style={{ width: '100%', fontFamily: sans, fontSize: 13, padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, background: T.bg, boxSizing: 'border-box' }} />
          </div>
          <button
            disabled={!feedback.trim()}
            onClick={() => onDeny({ feedback: feedback.trim(), aiNotes: aiNotes.trim(), chips, reviewer: reviewer.trim() || 'Admin' })}
            style={{
              padding: '10px 22px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: !feedback.trim() ? '#ccc' : T.red,
              color: '#fff', fontFamily: sans, fontSize: 14, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap',
            }}
          >
            <XCircle size={17} />
            Deny Product
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Propose Modal (from Ideas tab) ──────────────────────────────────────────
function ProposeModal({
  idea, onClose, onPropose,
}: {
  idea: typeof PRODUCT_IDEAS[0];
  onClose: () => void;
  onPropose: (p: { name: string; tagline: string; description: string; categorySlug: string; price: number; cost: number; notes: string }) => void;
}) {
  const [name, setName]       = useState(idea.name);
  const [tagline, setTagline] = useState(idea.vibe);
  const [desc, setDesc]       = useState(idea.desc);
  const [price, setPrice]     = useState(String(idea.price));
  const [cost, setCost]       = useState(String(idea.cost));
  const [notes, setNotes]     = useState('');
  const cat = CAT[idea.cat] ?? CAT.other;
  const priceNum = parseFloat(price) || 0;
  const costNum  = parseFloat(cost)  || 0;
  const slug = slugify(name);
  const mgn = margin(priceNum, costNum);

  return (
    <Modal onClose={onClose}>
      <ModalHeader title={`Propose: ${idea.name}`} onClose={onClose} />
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: `${cat.color}18`, borderRadius: 20, padding: '4px 12px', width: 'fit-content' }}>
          <span>{cat.icon}</span>
          <span style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: cat.color }}>{cat.label}</span>
        </div>

        {[
          { label: 'PRODUCT NAME', val: name, set: setName },
          { label: 'TAGLINE / VIBE', val: tagline, set: setTagline },
        ].map(({ label, val, set }) => (
          <div key={label}>
            <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>{label}</label>
            <input value={val} onChange={e => set(e.target.value)} style={{ width: '100%', fontFamily: sans, fontSize: 13, padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, background: T.bg, boxSizing: 'border-box' }} />
          </div>
        ))}

        <div>
          <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>DESCRIPTION</label>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} style={{ width: '100%', fontFamily: sans, fontSize: 13, padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, background: T.bg, resize: 'vertical', boxSizing: 'border-box' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            { label: 'ESTIMATED COST ($)', val: cost, set: setCost },
            { label: 'SUGGESTED PRICE ($)', val: price, set: setPrice },
          ].map(({ label, val, set }) => (
            <div key={label}>
              <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>{label}</label>
              <input type="number" step="0.01" min="0" value={val} onChange={e => set(e.target.value)} style={{ width: '100%', fontFamily: sans, fontSize: 14, fontWeight: 700, padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 6, color: T.navy, background: '#fff', boxSizing: 'border-box' }} />
            </div>
          ))}
        </div>
        {mgn !== null && (
          <div style={{ fontFamily: sans, fontSize: 12, color: marginColor(mgn), fontWeight: 600 }}>
            Projected margin: {mgn}%
          </div>
        )}

        <div>
          <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>NOTES FOR AI IMAGE GENERATION</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe what you want Gemini to generate — style, angle, colors, props..." rows={3} style={{ width: '100%', fontFamily: sans, fontSize: 13, padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, background: T.bg, resize: 'vertical', boxSizing: 'border-box' }} />
        </div>

        {/* Expected image filename */}
        <div style={{ background: '#1B1B1B', borderRadius: 8, padding: '10px 14px' }}>
          <div style={{ fontFamily: sans, fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.1em', marginBottom: 4 }}>EXPECTED IMAGE PATH</div>
          <code style={{ fontFamily: 'monospace', fontSize: 12, color: '#7EE787' }}>public/merch/ahp-{slug}.png</code>
        </div>

        <button
          disabled={!name.trim() || !priceNum}
          onClick={() => onPropose({ name: name.trim(), tagline: tagline.trim(), description: desc.trim(), categorySlug: idea.cat, price: priceNum, cost: costNum, notes: notes.trim() })}
          style={{
            padding: '12px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: (!name.trim() || !priceNum) ? '#ccc' : T.navy,
            color: '#fff', fontFamily: sans, fontSize: 14, fontWeight: 700,
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <Plus size={17} />
          Add to Generation Queue
        </button>
      </div>
    </Modal>
  );
}

// ─── Setup Screen ─────────────────────────────────────────────────────────────
function SetupScreen({ onRetry }: { onRetry: () => void }) {
  const [copied, setCopied] = useState(false);
  const sql = `psql -h 127.0.0.1 -p 5432 -U postgres -d postgres -f sql/create_merch_tables.sql`;
  return (
    <div style={{ maxWidth: 640, margin: '40px auto', padding: 24 }}>
      <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <AlertTriangle size={22} color="#D97706" />
          <div>
            <div style={{ fontFamily: serif, fontSize: 18, color: '#92400E', marginBottom: 6 }}>Database Setup Required</div>
            <p style={{ fontFamily: sans, fontSize: 13, color: '#78350F', margin: 0, lineHeight: 1.6 }}>
              The merch tables don't exist yet. Run the migration to set up the database, then come back.
            </p>
          </div>
        </div>
      </div>
      <div style={{ fontFamily: sans, fontSize: 13, color: T.textMuted, marginBottom: 8, fontWeight: 700, letterSpacing: '0.08em' }}>STEP 1 — RUN MIGRATION</div>
      <div style={{ background: '#1B1B1B', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <code style={{ fontFamily: 'monospace', fontSize: 11, color: '#7EE787', flex: 1, wordBreak: 'break-all' }}>
            PGPASSWORD='...' {sql}
          </code>
          <button onClick={() => { navigator.clipboard.writeText(`PGPASSWORD='YOURPASSWORD' ${sql}`); setCopied(true); setTimeout(() => setCopied(false), 2000); }} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 5, padding: '6px 10px', cursor: 'pointer', color: copied ? '#7EE787' : 'rgba(255,255,255,0.7)', flexShrink: 0 }}>
            <Copy size={13} />
          </button>
        </div>
      </div>
      <p style={{ fontFamily: sans, fontSize: 13, color: T.textMuted, marginBottom: 16 }}>Or open the Supabase dashboard → SQL Editor → paste <code>sql/create_merch_tables.sql</code></p>
      <button onClick={onRetry} style={{ display: 'flex', alignItems: 'center', gap: 8, background: T.navy, color: '#FAF9F6', border: 'none', borderRadius: 8, padding: '10px 20px', fontFamily: sans, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
        <RefreshCw size={15} />
        Try Again
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export function MerchManager() {
  const { toast }       = useToast();
  const qc              = useQueryClient();
  const [subTab, setSubTab] = useState<SubTab>('queue');
  const [approving, setApproving] = useState<MerchProduct | null>(null);
  const [denying,   setDenying]   = useState<MerchProduct | null>(null);
  const [proposing, setProposing] = useState<typeof PRODUCT_IDEAS[0] | null>(null);
  const [reviewerName, setReviewerName] = useState('Admin');
  const [ideaCat, setIdeaCat] = useState<string>('all');
  const [expandedDenied, setExpandedDenied] = useState<string | null>(null);
  const [newMotto, setNewMotto] = useState('');
  const [newMottoCategory, setNewMottoCategory] = useState<MottoCategory>('general');
  const [newMottoSubmitter, setNewMottoSubmitter] = useState('');

  // Get reviewer name from auth session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email) {
        setReviewerName(session.user.email.split('@')[0]);
      }
    });
  }, []);

  // Load all products
  const { data: products = [], isLoading, error, refetch } = useQuery({
    queryKey: ['merch_products'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('merch_products')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as MerchProduct[];
    },
    refetchInterval: 60_000,
  });

  // Load reviews
  const { data: reviews = [] } = useQuery({
    queryKey: ['merch_reviews'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('merch_product_reviews')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as MerchReview[];
    },
  });

  // Load mottos
  const { data: mottos = [], refetch: refetchMottos } = useQuery({
    queryKey: ['brand_mottos'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('brand_mottos')
        .select('*')
        .eq('brand', 'ahp')
        .order('votes', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as BrandMotto[];
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['merch_products'] });
    qc.invalidateQueries({ queryKey: ['merch_reviews'] });
  };

  const invalidateMottos = () => {
    qc.invalidateQueries({ queryKey: ['brand_mottos'] });
  };

  // ── Approve mutation ─────────────────────────────────────────────────────────
  const approveMutation = useMutation({
    mutationFn: async ({ id, price, badge, name, tagline, reviewer }: { id: string; price: number; badge: string; name: string; tagline: string; reviewer: string }) => {
      const { error: e1 } = await (supabase as any).from('merch_products').update({
        status: 'approved', price, badge: badge || null,
        name, tagline, approved_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      }).eq('id', id);
      if (e1) throw e1;
      const { error: e2 } = await (supabase as any).from('merch_product_reviews').insert({
        product_id: id, reviewer_name: reviewer, action: 'approved', price_set: price,
      });
      if (e2) throw e2;
    },
    onSuccess: () => { invalidate(); toast({ title: '✅ Approved & Published!', description: 'Product is now live in the store.' }); setApproving(null); },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  // ── Deny mutation ────────────────────────────────────────────────────────────
  const denyMutation = useMutation({
    mutationFn: async ({ id, feedback, aiNotes, chips, reviewer }: { id: string; feedback: string; aiNotes: string; chips: string[]; reviewer: string }) => {
      const fullFeedback = [chips.join(', '), feedback, aiNotes ? `AI notes: ${aiNotes}` : ''].filter(Boolean).join('\n\n');
      const { error: e1 } = await (supabase as any).from('merch_products').update({
        status: 'denied', denied_at: new Date().toISOString(), generation_notes: aiNotes || null, updated_at: new Date().toISOString(),
      }).eq('id', id);
      if (e1) throw e1;
      const { error: e2 } = await (supabase as any).from('merch_product_reviews').insert({
        product_id: id, reviewer_name: reviewer, action: 'denied', feedback: fullFeedback,
      });
      if (e2) throw e2;
    },
    onSuccess: () => { invalidate(); toast({ title: 'Product denied', description: 'Feedback saved.' }); setDenying(null); },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  // ── Restore mutation ─────────────────────────────────────────────────────────
  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error: e1 } = await (supabase as any).from('merch_products').update({ status: 'pending_review', denied_at: null, updated_at: new Date().toISOString() }).eq('id', id);
      if (e1) throw e1;
      const { error: e2 } = await (supabase as any).from('merch_product_reviews').insert({ product_id: id, reviewer_name: reviewerName, action: 'restored' });
      if (e2) throw e2;
    },
    onSuccess: () => { invalidate(); toast({ title: 'Restored to queue' }); },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  // ── Archive mutation ─────────────────────────────────────────────────────────
  const archiveMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from('merch_products').update({ status: 'archived', updated_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast({ title: 'Product archived', description: 'Pulled from store.' }); },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  // ── Update price inline ─────────────────────────────────────────────────────
  const priceMutation = useMutation({
    mutationFn: async ({ id, price }: { id: string; price: number }) => {
      const { error } = await (supabase as any).from('merch_products').update({ price, updated_at: new Date().toISOString() }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast({ title: 'Price updated' }); },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  // ── Propose mutation ─────────────────────────────────────────────────────────
  const proposeMutation = useMutation({
    mutationFn: async ({ name, tagline, description, categorySlug, price, cost, notes }: any) => {
      const { error } = await (supabase as any).from('merch_products').insert({
        name, tagline, description, category_slug: categorySlug, brand: 'ahp',
        status: 'draft', price, cost_estimate: cost, generation_notes: notes || null,
        image_path: `/merch/ahp-${slugify(name)}.png`,
      });
      if (error) throw error;
    },
    onSuccess: () => { invalidate(); toast({ title: '💡 Added to generation queue!', description: 'Generate the image with Gemini, then mark as Ready.' }); setProposing(null); setSubTab('ideas'); },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  // ── Motto mutations ───────────────────────────────────────────────────────
  const addMottoMutation = useMutation({
    mutationFn: async ({ motto, category, submitted_by }: { motto: string; category: MottoCategory; submitted_by: string }) => {
      const { error } = await (supabase as any).from('brand_mottos').insert({
        motto, category, submitted_by: submitted_by || 'Anonymous', brand: 'ahp', status: 'draft',
      });
      if (error) throw error;
    },
    onSuccess: () => { invalidateMottos(); toast({ title: 'Motto added!', description: 'Added to brainstorm list.' }); setNewMotto(''); },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const updateMottoMutation = useMutation({
    mutationFn: async ({ id, status, votes }: { id: string; status?: MottoStatus; votes?: number }) => {
      const update: any = { updated_at: new Date().toISOString() };
      if (status !== undefined) update.status = status;
      if (votes !== undefined) update.votes = votes;
      const { error } = await (supabase as any).from('brand_mottos').update(update).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { invalidateMottos(); },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const deleteMottoMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from('brand_mottos').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { invalidateMottos(); toast({ title: 'Motto removed' }); },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  // ── Filtered views ─────────────────────────────────────────────────────────
  const queue   = products.filter(p => p.status === 'pending_review');
  const live    = products.filter(p => p.status === 'approved');
  const denied  = products.filter(p => p.status === 'denied');
  const drafts  = products.filter(p => p.status === 'draft');
  const ideaList = PRODUCT_IDEAS.filter(i => ideaCat === 'all' || i.cat === ideaCat);

  const approvedMottos = mottos.filter(m => m.status === 'approved');
  const draftMottos    = mottos.filter(m => m.status === 'draft');

  const SUB_TABS: { id: SubTab; label: string; count?: number }[] = [
    { id: 'queue',  label: 'Approval Queue', count: queue.length },
    { id: 'live',   label: 'Live Products',  count: live.length },
    { id: 'denied', label: 'Denied',         count: denied.length },
    { id: 'ideas',  label: 'Ideas & Queue',  count: drafts.length || undefined },
    { id: 'mottos', label: 'Mottos & Phrases', count: draftMottos.length || undefined },
    { id: 'all',    label: 'All Products',   count: products.length },
  ];

  // ── Detect setup needed ────────────────────────────────────────────────────
  const needsSetup = !!error && ((error as any)?.message?.includes('does not exist') || (error as any)?.code === 'PGRST204' || (error as any)?.code === '42P01');

  if (needsSetup) return <SetupScreen onRetry={() => refetch()} />;

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: sans }}>

      {/* Sub-tab nav */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap' }}>
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: sans, fontSize: 13, fontWeight: subTab === t.id ? 700 : 500,
            color: subTab === t.id ? T.navy : T.textMuted,
            borderBottom: subTab === t.id ? `3px solid ${T.red}` : '3px solid transparent',
            marginBottom: -1, whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span style={{ background: subTab === t.id ? T.red : 'rgba(0,40,104,0.12)', color: subTab === t.id ? '#fff' : T.navy, borderRadius: 20, padding: '0 7px', fontSize: 11, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── APPROVAL QUEUE ─────────────────────────────────────────────────── */}
      {subTab === 'queue' && (
        <div>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 48, color: T.textMuted, fontFamily: sans }}>Loading products…</div>
          ) : queue.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 64 }}>
              <CheckCircle size={40} color={T.green} style={{ opacity: 0.4, marginBottom: 12 }} />
              <div style={{ fontFamily: serif, fontSize: 22, color: T.navy, marginBottom: 8 }}>All caught up!</div>
              <div style={{ fontFamily: sans, fontSize: 14, color: T.textMuted }}>No products waiting for review. Go propose some ideas.</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 20 }}>
              {queue.map(product => {
                const cat = CAT[product.category_slug] ?? CAT.other;
                return (
                  <div key={product.id} style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden', boxShadow: T.shadow }}>
                    {/* Image */}
                    <div style={{ height: 200, background: 'rgba(0,40,104,0.04)', position: 'relative', overflow: 'hidden' }}>
                      {product.image_path ? (
                        <img src={product.image_path} alt={product.name} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; console.warn('Image failed:', product.image_path); }} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: T.textMuted }}>
                          <Package size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                          <div style={{ fontSize: 11 }}>No image yet</div>
                        </div>
                      )}
                      <div style={{ position: 'absolute', top: 8, left: 8, background: `${cat.color}DD`, borderRadius: 16, padding: '3px 10px' }}>
                        <span style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: '#fff' }}>{cat.icon} {cat.label}</span>
                      </div>
                      <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.55)', borderRadius: 6, padding: '3px 8px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#fff', opacity: 0.8 }}>{fmtDate(product.created_at)}</span>
                      </div>
                    </div>

                    {/* Info */}
                    <div style={{ padding: '14px 16px' }}>
                      <div style={{ fontFamily: serif, fontSize: 17, color: T.navy, marginBottom: 3, lineHeight: 1.3 }}>{product.name}</div>
                      {product.tagline && <div style={{ fontFamily: sans, fontSize: 12, color: T.red, marginBottom: 6 }}>{product.tagline}</div>}
                      <div style={{ fontFamily: sans, fontSize: 12.5, color: T.textMuted, lineHeight: 1.5, marginBottom: 10, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {product.description}
                      </div>
                      {product.cost_estimate && (
                        <div style={{ fontFamily: sans, fontSize: 11, color: T.textMuted, marginBottom: 12 }}>
                          Est. cost: <b>${product.cost_estimate.toFixed(2)}</b>
                          {product.price && <span style={{ marginLeft: 8, color: marginColor(margin(product.price, product.cost_estimate)), fontWeight: 700 }}> → ${product.price.toFixed(2)} ({margin(product.price, product.cost_estimate)}% margin)</span>}
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setApproving(product)} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: 'none', background: T.green, color: '#fff', fontFamily: sans, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <CheckCircle size={15} /> Approve
                        </button>
                        <button onClick={() => setDenying(product)} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: 'none', background: '#FDE8E8', color: T.red, fontFamily: sans, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <XCircle size={15} /> Deny
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── LIVE PRODUCTS ──────────────────────────────────────────────────── */}
      {subTab === 'live' && (
        <div>
          {live.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 64, color: T.textMuted, fontFamily: sans }}>No approved products yet. Approve some from the queue!</div>
          ) : (
            <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: T.navy }}>
                    {['Product', 'Category', 'Price', 'Margin', 'Badge', 'Approved', 'Action'].map(h => (
                      <th key={h} style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: 'rgba(250,249,246,0.70)', letterSpacing: '0.1em', padding: '10px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {live.map((p, i) => {
                    const cat = CAT[p.category_slug] ?? CAT.other;
                    const mgn = margin(p.price, p.cost_estimate);
                    return (
                      <LiveRow key={p.id} product={p} cat={cat} mgn={mgn} stripe={i % 2 === 0}
                        onPriceUpdate={(price) => priceMutation.mutate({ id: p.id, price })}
                        onArchive={() => archiveMutation.mutate(p.id)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── DENIED ─────────────────────────────────────────────────────────── */}
      {subTab === 'denied' && (
        <div>
          {denied.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 64, color: T.textMuted, fontFamily: sans }}>No denied products. Great taste!</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {denied.map(p => {
                const cat = CAT[p.category_slug] ?? CAT.other;
                const productReviews = reviews.filter(r => r.product_id === p.id && r.action === 'denied');
                const latestFeedback = productReviews[0];
                const isExpanded = expandedDenied === p.id;
                return (
                  <div key={p.id} style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', boxShadow: T.shadow }}>
                    <div style={{ display: 'flex', gap: 16, padding: '14px 16px', alignItems: 'flex-start' }}>
                      {p.image_path && <img src={p.image_path} alt={p.name} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                          <div style={{ fontFamily: serif, fontSize: 17, color: T.navy }}>{p.name}</div>
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button onClick={() => restoreMutation.mutate(p.id)} style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${T.navy}`, background: '#fff', color: T.navy, fontFamily: sans, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <RotateCcw size={12} /> Restore
                            </button>
                          </div>
                        </div>
                        <div style={{ fontFamily: sans, fontSize: 12, color: T.textMuted, marginBottom: 6 }}>
                          {cat.icon} {cat.label} · Denied {p.denied_at ? fmtDate(p.denied_at) : ''}
                          {latestFeedback && <span> · by {latestFeedback.reviewer_name}</span>}
                        </div>
                        {latestFeedback?.feedback && (
                          <div style={{ fontFamily: sans, fontSize: 13, color: T.text, background: '#FDE8E8', borderRadius: 6, padding: '8px 12px', lineHeight: 1.5 }}>
                            {isExpanded ? latestFeedback.feedback : latestFeedback.feedback.slice(0, 120) + (latestFeedback.feedback.length > 120 ? '…' : '')}
                          </div>
                        )}
                        {latestFeedback?.feedback && latestFeedback.feedback.length > 120 && (
                          <button onClick={() => setExpandedDenied(isExpanded ? null : p.id)} style={{ background: 'none', border: 'none', color: T.navy, fontFamily: sans, fontSize: 12, cursor: 'pointer', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                            {isExpanded ? <><ChevronUp size={12} /> Show less</> : <><ChevronDown size={12} /> Show all feedback</>}
                          </button>
                        )}
                        {p.generation_notes && (
                          <div style={{ fontFamily: sans, fontSize: 12, color: '#5B4C00', background: '#FFF8E1', borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>
                            <b>AI regeneration notes:</b> {p.generation_notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── IDEAS & GENERATION QUEUE ────────────────────────────────────────── */}
      {subTab === 'ideas' && (
        <div>
          {/* Generation Queue (drafts) */}
          {drafts.length > 0 && (
            <div style={{ marginBottom: 36 }}>
              <div style={{ fontFamily: serif, fontSize: 22, color: T.navy, marginBottom: 4 }}>⏳ Generation Queue</div>
              <div style={{ fontFamily: sans, fontSize: 13, color: T.textMuted, marginBottom: 16 }}>These products need Gemini images generated, then mark as Ready.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {drafts.map(p => {
                  const cat = CAT[p.category_slug] ?? CAT.other;
                  const slug = slugify(p.name);
                  return (
                    <div key={p.id} style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: T.shadow }}>
                      <span style={{ fontSize: 24 }}>{cat.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: serif, fontSize: 16, color: T.navy }}>{p.name}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: 11, color: T.textMuted, marginTop: 3 }}>
                          Image: public/merch/ahp-{slug}.png
                        </div>
                        {p.generation_notes && <div style={{ fontFamily: sans, fontSize: 12, color: '#78350F', background: '#FEF3C7', borderRadius: 4, padding: '3px 8px', marginTop: 4, display: 'inline-block' }}>{p.generation_notes}</div>}
                      </div>
                      <button
                        onClick={() => (supabase as any).from('merch_products').update({ status: 'pending_review', updated_at: new Date().toISOString() }).eq('id', p.id).then(() => invalidate())}
                        style={{ padding: '8px 14px', borderRadius: 7, border: `1px solid ${T.green}`, background: 'rgba(43,94,73,0.08)', color: T.green, fontFamily: sans, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        Mark as Ready ✓
                      </button>
                    </div>
                  );
                })}
              </div>
              <div style={{ height: 1, background: T.border, margin: '32px 0' }} />
            </div>
          )}

          {/* Idea catalog */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ fontFamily: serif, fontSize: 22, color: T.navy }}>💡 Product Ideas Catalog</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['all', ...Object.keys(CAT).filter(k => k !== 'other')].map(c => (
                <button key={c} onClick={() => setIdeaCat(c)} style={{
                  padding: '5px 12px', borderRadius: 20, fontFamily: sans, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: ideaCat === c ? `2px solid ${T.navy}` : `1px solid ${T.border}`,
                  background: ideaCat === c ? T.navy : '#fff',
                  color: ideaCat === c ? '#FAF9F6' : T.textMuted,
                }}>
                  {c === 'all' ? 'All' : CAT[c]?.icon + ' ' + CAT[c]?.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {ideaList.map(idea => {
              const cat = CAT[idea.cat] ?? CAT.other;
              const mgn = margin(idea.price, idea.cost);
              const alreadyProposed = products.some(p => p.name.toLowerCase() === idea.name.toLowerCase());
              return (
                <div key={idea.name} style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, padding: '16px 18px', boxShadow: T.shadow, opacity: alreadyProposed ? 0.6 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>{cat.icon}</span>
                    <span style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: cat.color }}>{cat.label}</span>
                    {alreadyProposed && <span style={{ marginLeft: 'auto', fontFamily: sans, fontSize: 11, color: T.green, fontWeight: 700 }}>✓ In catalog</span>}
                  </div>
                  <div style={{ fontFamily: serif, fontSize: 17, color: T.navy, marginBottom: 5 }}>{idea.name}</div>
                  <div style={{ fontFamily: sans, fontSize: 12.5, color: T.textMuted, lineHeight: 1.5, marginBottom: 8 }}>{idea.desc}</div>
                  <div style={{ fontFamily: sans, fontSize: 12, color: T.textMuted, marginBottom: 12, fontStyle: 'italic' }}>{idea.vibe}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <span style={{ fontFamily: sans, fontSize: 13, color: T.textMuted }}>~${idea.cost.toFixed(2)} cost → </span>
                      <span style={{ fontFamily: serif, fontSize: 16, color: T.red, fontWeight: 400 }}>${idea.price.toFixed(2)}</span>
                      {mgn !== null && <span style={{ fontFamily: sans, fontSize: 11, color: marginColor(mgn), fontWeight: 700, marginLeft: 6 }}>({mgn}%)</span>}
                    </div>
                    <button
                      onClick={() => !alreadyProposed && setProposing(idea)}
                      disabled={alreadyProposed}
                      style={{ padding: '7px 13px', borderRadius: 7, border: 'none', background: alreadyProposed ? '#eee' : T.navy, color: alreadyProposed ? T.textMuted : '#FAF9F6', fontFamily: sans, fontSize: 12, fontWeight: 700, cursor: alreadyProposed ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
                    >
                      <Plus size={13} /> Propose
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── MOTTOS & PHRASES ────────────────────────────────────────────────── */}
      {subTab === 'mottos' && (
        <div>
          {/* Add new motto form */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 12, padding: '24px 28px', marginBottom: 28, boxShadow: T.shadow }}>
            <div style={{ fontFamily: serif, fontSize: 22, color: T.navy, marginBottom: 4 }}>Add a Motto or Phrase</div>
            <div style={{ fontFamily: sans, fontSize: 13, color: T.textMuted, marginBottom: 18, lineHeight: 1.5 }}>
              Brainstorm slogans, taglines, and catchphrases for the AHP brand. Approved mottos are used by the merch generator for product copy.
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 300px' }}>
                <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>MOTTO / PHRASE</label>
                <input
                  value={newMotto}
                  onChange={e => setNewMotto(e.target.value)}
                  placeholder={'e.g. "Don\'t Get Billed Blind"'}
                  onKeyDown={e => e.key === 'Enter' && newMotto.trim() && addMottoMutation.mutate({ motto: newMotto.trim(), category: newMottoCategory, submitted_by: newMottoSubmitter.trim() || reviewerName })}
                  style={{ width: '100%', fontFamily: sans, fontSize: 15, padding: '10px 12px', border: `2px solid ${T.navy}`, borderRadius: 8, color: T.text, background: '#fff', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: '0 0 160px' }}>
                <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>TYPE</label>
                <select
                  value={newMottoCategory}
                  onChange={e => setNewMottoCategory(e.target.value as MottoCategory)}
                  style={{ width: '100%', fontFamily: sans, fontSize: 13, padding: '10px 8px', border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, background: '#fff', boxSizing: 'border-box' }}
                >
                  <option value="general">General</option>
                  <option value="tagline">Tagline</option>
                  <option value="bumper">Bumper Sticker</option>
                  <option value="apparel">Apparel</option>
                  <option value="social">Social Media</option>
                  <option value="hashtag">Hashtag</option>
                  <option value="call_to_action">Call to Action</option>
                </select>
              </div>
              <div style={{ flex: '0 0 140px' }}>
                <label style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>YOUR NAME</label>
                <input
                  value={newMottoSubmitter}
                  onChange={e => setNewMottoSubmitter(e.target.value)}
                  placeholder={reviewerName}
                  style={{ width: '100%', fontFamily: sans, fontSize: 13, padding: '10px 8px', border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, background: '#fff', boxSizing: 'border-box' }}
                />
              </div>
              <button
                disabled={!newMotto.trim()}
                onClick={() => addMottoMutation.mutate({ motto: newMotto.trim(), category: newMottoCategory, submitted_by: newMottoSubmitter.trim() || reviewerName })}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: !newMotto.trim() ? '#ccc' : T.navy,
                  color: '#fff', fontFamily: sans, fontSize: 14, fontWeight: 700,
                  display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                }}
              >
                <Plus size={15} /> Add
              </button>
            </div>
          </div>

          {/* Approved mottos */}
          {approvedMottos.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontFamily: serif, fontSize: 20, color: T.navy, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircle size={18} color={T.green} /> Approved — Live for Merch
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {approvedMottos.map(m => {
                  const MOTTO_CAT_COLORS: Record<string, { bg: string; color: string; label: string }> = {
                    general:        { bg: 'rgba(0,40,104,0.08)',   color: T.navy,  label: 'General' },
                    tagline:        { bg: 'rgba(197,165,90,0.15)', color: '#8B7032', label: 'Tagline' },
                    bumper:         { bg: 'rgba(178,34,52,0.10)',  color: T.red,    label: 'Bumper' },
                    apparel:        { bg: 'rgba(43,94,73,0.10)',   color: T.green,  label: 'Apparel' },
                    social:         { bg: 'rgba(107,76,138,0.10)', color: '#6B4C8A', label: 'Social' },
                    hashtag:        { bg: 'rgba(212,118,59,0.10)', color: '#D4763B', label: 'Hashtag' },
                    call_to_action: { bg: 'rgba(178,34,52,0.10)',  color: T.red,    label: 'CTA' },
                  };
                  const mc = MOTTO_CAT_COLORS[m.category] ?? MOTTO_CAT_COLORS.general;
                  return (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 16px', boxShadow: T.shadow }}>
                      <div style={{ fontFamily: serif, fontSize: 17, color: T.navy, flex: 1 }}>"{m.motto}"</div>
                      <span style={{ background: mc.bg, color: mc.color, fontFamily: sans, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{mc.label}</span>
                      <span style={{ fontFamily: sans, fontSize: 11, color: T.textMuted }}>{m.submitted_by}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <button
                          onClick={() => updateMottoMutation.mutate({ id: m.id, votes: m.votes + 1 })}
                          style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontFamily: sans, fontSize: 12, fontWeight: 700, color: T.navy, display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          <TrendingUp size={12} /> {m.votes}
                        </button>
                      </div>
                      <button
                        onClick={() => updateMottoMutation.mutate({ id: m.id, status: 'archived' })}
                        style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: T.textMuted }}
                      >
                        <Archive size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Draft mottos (brainstorm) */}
          <div>
            <div style={{ fontFamily: serif, fontSize: 20, color: T.navy, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Edit3 size={18} color={T.gold} /> Brainstorm — Pending Review
            </div>
            {draftMottos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: T.textMuted, fontFamily: sans }}>
                No draft mottos yet. Add one above to start brainstorming!
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {draftMottos.map(m => {
                  const MOTTO_CAT_COLORS: Record<string, { bg: string; color: string; label: string }> = {
                    general:        { bg: 'rgba(0,40,104,0.08)',   color: T.navy,  label: 'General' },
                    tagline:        { bg: 'rgba(197,165,90,0.15)', color: '#8B7032', label: 'Tagline' },
                    bumper:         { bg: 'rgba(178,34,52,0.10)',  color: T.red,    label: 'Bumper' },
                    apparel:        { bg: 'rgba(43,94,73,0.10)',   color: T.green,  label: 'Apparel' },
                    social:         { bg: 'rgba(107,76,138,0.10)', color: '#6B4C8A', label: 'Social' },
                    hashtag:        { bg: 'rgba(212,118,59,0.10)', color: '#D4763B', label: 'Hashtag' },
                    call_to_action: { bg: 'rgba(178,34,52,0.10)',  color: T.red,    label: 'CTA' },
                  };
                  const mc = MOTTO_CAT_COLORS[m.category] ?? MOTTO_CAT_COLORS.general;
                  return (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, padding: '12px 16px', boxShadow: T.shadow }}>
                      <div style={{ fontFamily: serif, fontSize: 17, color: T.navy, flex: 1 }}>"{m.motto}"</div>
                      <span style={{ background: mc.bg, color: mc.color, fontFamily: sans, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{mc.label}</span>
                      <span style={{ fontFamily: sans, fontSize: 11, color: T.textMuted }}>{m.submitted_by}</span>
                      <button
                        onClick={() => updateMottoMutation.mutate({ id: m.id, status: 'approved' })}
                        style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: T.green, color: '#fff', fontFamily: sans, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <CheckCircle size={12} /> Approve
                      </button>
                      <button
                        onClick={() => deleteMottoMutation.mutate(m.id)}
                        style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: T.textMuted }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ALL PRODUCTS ────────────────────────────────────────────────────── */}
      {subTab === 'all' && (
        <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: T.navy }}>
                {['', 'Product', 'Category', 'Status', 'Price', 'Margin', 'Created'].map(h => (
                  <th key={h} style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, color: 'rgba(250,249,246,0.70)', letterSpacing: '0.1em', padding: '10px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => {
                const cat = CAT[p.category_slug] ?? CAT.other;
                const mgn = margin(p.price, p.cost_estimate);
                const STATUS_COLORS: Record<Status, { bg: string; color: string }> = {
                  draft:          { bg: '#F3F4F6', color: '#6B7280' },
                  pending_review: { bg: '#FEF3C7', color: '#92400E' },
                  approved:       { bg: '#D1FAE5', color: '#065F46' },
                  denied:         { bg: '#FEE2E2', color: '#991B1B' },
                  archived:       { bg: '#F3F4F6', color: '#6B7280' },
                };
                const sc = STATUS_COLORS[p.status] ?? STATUS_COLORS.draft;
                return (
                  <tr key={p.id} style={{ background: i % 2 === 0 ? '#fff' : T.bg, borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: '10px 14px', width: 48 }}>
                      {p.image_path && <img src={p.image_path} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 5 }} />}
                    </td>
                    <td style={{ padding: '10px 14px', fontFamily: serif, fontSize: 14, color: T.navy }}>{p.name}</td>
                    <td style={{ padding: '10px 14px', fontFamily: sans, fontSize: 12, color: T.textMuted }}>{cat.icon} {cat.label}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: sc.bg, color: sc.color, fontFamily: sans, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, textTransform: 'capitalize' }}>
                        {p.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', fontFamily: sans, fontSize: 14, color: T.text, fontWeight: 600 }}>
                      {p.price ? `$${p.price.toFixed(2)}` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontFamily: sans, fontSize: 13, fontWeight: 700, color: marginColor(mgn) }}>
                      {mgn !== null ? `${mgn}%` : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontFamily: sans, fontSize: 12, color: T.textMuted }}>{fmtDate(p.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      {approving && (
        <ApproveModal
          product={approving}
          reviewerName={reviewerName}
          onClose={() => setApproving(null)}
          onApprove={({ price, badge, name, tagline, reviewer }) =>
            approveMutation.mutate({ id: approving.id, price, badge, name, tagline, reviewer })
          }
        />
      )}
      {denying && (
        <DenyModal
          product={denying}
          reviewerName={reviewerName}
          onClose={() => setDenying(null)}
          onDeny={({ feedback, aiNotes, chips, reviewer }) =>
            denyMutation.mutate({ id: denying.id, feedback, aiNotes, chips, reviewer })
          }
        />
      )}
      {proposing && (
        <ProposeModal
          idea={proposing}
          onClose={() => setProposing(null)}
          onPropose={(p) => proposeMutation.mutate(p)}
        />
      )}
    </div>
  );
}

// ─── Live Products Row (with inline price edit) ───────────────────────────────
function LiveRow({
  product, cat, mgn, stripe, onPriceUpdate, onArchive,
}: {
  product: MerchProduct;
  cat: { icon: string; label: string; color: string };
  mgn: number | null;
  stripe: boolean;
  onPriceUpdate: (price: number) => void;
  onArchive: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [priceVal, setPriceVal] = useState(String(product.price ?? ''));

  const save = () => {
    const n = parseFloat(priceVal);
    if (n > 0) onPriceUpdate(n);
    setEditing(false);
  };

  return (
    <tr style={{ background: stripe ? '#fff' : T.bg, borderBottom: `1px solid ${T.border}` }}>
      <td style={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {product.image_path && <img src={product.image_path} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 5 }} />}
          <div>
            <div style={{ fontFamily: serif, fontSize: 14, color: T.navy }}>{product.name}</div>
            {product.badge && <span style={{ fontFamily: sans, fontSize: 10, fontWeight: 700, color: T.red, background: '#FDE8E8', borderRadius: 10, padding: '1px 7px' }}>{product.badge}</span>}
          </div>
        </div>
      </td>
      <td style={{ padding: '10px 14px', fontFamily: sans, fontSize: 12, color: T.textMuted }}>{cat.icon} {cat.label}</td>
      <td style={{ padding: '10px 14px' }}>
        {editing ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" step="0.01" value={priceVal} onChange={e => setPriceVal(e.target.value)} onKeyDown={e => e.key === 'Enter' && save()} autoFocus style={{ width: 70, fontFamily: sans, fontSize: 13, fontWeight: 700, padding: '4px 6px', border: `2px solid ${T.navy}`, borderRadius: 5, color: T.navy }} />
            <button onClick={save} style={{ background: T.green, color: '#fff', border: 'none', borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Save</button>
            <button onClick={() => setEditing(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted }}><X size={14} /></button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} style={{ background: 'none', border: `1px dashed ${T.border}`, borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontFamily: sans, fontSize: 14, fontWeight: 700, color: T.navy, display: 'flex', alignItems: 'center', gap: 6 }}>
            ${product.price?.toFixed(2) ?? '—'}
            <Edit3 size={11} color={T.textMuted} />
          </button>
        )}
      </td>
      <td style={{ padding: '10px 14px', fontFamily: sans, fontSize: 13, fontWeight: 700, color: marginColor(mgn) }}>
        {mgn !== null ? `${mgn}%` : '—'}
      </td>
      <td style={{ padding: '10px 14px' }}>
        {product.badge ? (
          <span style={{ background: '#FDE8E8', color: T.red, fontFamily: sans, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>{product.badge}</span>
        ) : <span style={{ color: T.textMuted, fontSize: 12 }}>—</span>}
      </td>
      <td style={{ padding: '10px 14px', fontFamily: sans, fontSize: 12, color: T.textMuted }}>
        {product.approved_at ? fmtDate(product.approved_at) : '—'}
      </td>
      <td style={{ padding: '10px 14px' }}>
        <button onClick={onArchive} style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontFamily: sans, fontSize: 11, color: T.textMuted, display: 'flex', alignItems: 'center', gap: 5 }}>
          <Archive size={12} /> Archive
        </button>
      </td>
    </tr>
  );
}

export default MerchManager;
